import { useCallback, useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiPost, formatDateTime, isApiError, useCsrf } from "@amber/ui";
import {
  correctionResponseSchema,
  leaseReleaseSchema,
  leaseViewSchema,
  type CorrectionChange,
  type CorrectionResponse,
  type EvidenceRecord,
  type LeaseView,
  type ProblemView,
  type TurnView,
} from "../../contracts/index";
import { evidenceKeys } from "../keys";

export function useEditing(itemId: string) {
  const csrf = useCsrf();
  const client = useQueryClient();
  const lease = useRef<LeaseView | null>(null);
  const timer = useRef<number | null>(null);

  const stopHeartbeat = () => {
    if (timer.current !== null) window.clearInterval(timer.current);
    timer.current = null;
  };
  const release = useCallback(async () => {
    stopHeartbeat();
    const held = lease.current;
    lease.current = null;
    if (held)
      await apiPost(`/api/evidence/leases/${held.lease_id}/release`, {}, leaseReleaseSchema, csrf()).catch(() => null);
  }, [csrf]);
  const acquire = useCallback(
    async (communicationId: string): Promise<LeaseView> => {
      if (lease.current?.communication_id === communicationId) return lease.current;
      const got = await apiPost(`/api/evidence/communications/${communicationId}/lease`, {}, leaseViewSchema, csrf());
      lease.current = got;
      stopHeartbeat();
      timer.current = window.setInterval(() => {
        const held = lease.current;
        if (!held) return;
        void apiPost(`/api/evidence/leases/${held.lease_id}/heartbeat`, {}, leaseViewSchema, csrf())
          .then((next) => {
            lease.current = next;
          })
          .catch(() => {
            stopHeartbeat();
          });
      }, got.heartbeat_ms);
      return got;
    },
    [csrf],
  );
  useEffect(
    () => () => {
      void release();
    },
    [release],
  );

  const save = useCallback(
    async (input: {
      communicationId: string;
      baseRevisionId: string;
      unitId: string;
      reason: string;
      changes: CorrectionChange[];
      resolves: string[];
    }): Promise<CorrectionResponse> => {
      const held = await acquire(input.communicationId);
      try {
        const out = await apiPost(
          `/api/evidence/communications/${input.communicationId}/corrections`,
          {
            lease_id: held.lease_id,
            base_revision_id: input.baseRevisionId,
            unit_id: input.unitId,
            reason: input.reason,
            changes: input.changes,
            resolves: input.resolves,
          },
          correctionResponseSchema,
          csrf(),
        );
        lease.current = null;
        stopHeartbeat();
        await client.invalidateQueries({ queryKey: evidenceKeys.recordsOf(itemId) });
        return out;
      } catch (e) {
        if (isApiError(e) && e.code === "conflict" && e.current === null) {
          lease.current = null;
          stopHeartbeat();
        }
        throw e;
      }
    },
    [acquire, client, csrf, itemId],
  );
  return useMemo(() => ({ acquire, release, save }), [acquire, release, save]);
}

export type Editing = ReturnType<typeof useEditing>;

export function communicationOf(record: EvidenceRecord, turn: TurnView) {
  return record.communications.find((c) => c.ordinal === turn.communication) ?? record.communications[0] ?? null;
}

export function OpenReports({
  problems,
  picked,
  onPick,
}: {
  problems: readonly ProblemView[];
  picked: readonly string[];
  onPick: (ids: string[]) => void;
}) {
  if (problems.length === 0) return null;
  return (
    <div
      role="group"
      aria-label="Open reports on this passage"
      className="mt-2 rounded-md border border-divider bg-bg px-2.5 py-2 text-md"
    >
      <p className="m-0 font-semibold">Open {problems.length > 1 ? "reports" : "report"} on this passage</p>
      {problems.map((p) => (
        <label key={p.id} className="mt-1 flex items-start gap-2">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={picked.includes(p.id)}
            onChange={(e) => {
              onPick(e.target.checked ? [...picked, p.id] : picked.filter((x) => x !== p.id));
            }}
          />
          <span>
            Resolved by this correction{" "}
            <span className="text-muted">
              · {p.kind_label}: {p.description} · {p.reported_by}, {formatDateTime(p.reported_at)}
            </span>
          </span>
        </label>
      ))}
      <p className="m-0 mt-1 text-muted">Reports you don't tick stay open.</p>
    </div>
  );
}
